import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Modal, Image, Badge, Form, Spinner, Alert } from 'react-bootstrap';
import { adminInstance } from '../services/axiosInstance';
import LoaderOverlay from '../Components/shared/Loader';

const ExpenseAdminApprovalPage = () => {
    const hierarchy = sessionStorage.getItem('role');
    const { tripId } = useParams();
    const navigate = useNavigate();
    const [request, setRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [auditAct, setAuditAct] = useState(false);
    const [error, setError] = useState('');
    const [approvalComments, setApprovalComments] = useState('');
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    const [processing, setProcessing] = useState(false);

    // Fetch request details
    useEffect(() => {
        if (hierarchy !== "Admin" && hierarchy !== "Auditor") {
            alert("You are not authorized to access this page. Please contact your administrator.");
            navigate('/');
        }

        const fetchRequest = async () => {
            try {
                const response = await adminInstance.get(`/expenserequest/GetTripById/${tripId}`);
                setRequest(response.data);
                setApprovalComments(response.data.PaymentDetails || '');
            } catch (err) {
                setError('Failed to load expense request');
                console.error('Error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchRequest();
    }, [tripId]);

    // Handle approval actions
    const handleApproveOrReject = async (status) => {
        try {
            if (!approvalComments) {
                alert('Please enter payment details !!!');
                return;
            }

            if (hierarchy === "Auditor" && auditAct === false) {
                alert('Please acknowledge and try again!!!');
                return;
            }

            setLoading(true);
            setProcessing(true);
            var obj = {
                IS_ADMIN: hierarchy === "Admin" ? true : false,
                REQ_ID: 0,
                STATUS: status,
                COMMENT: approvalComments,
                ROLE: hierarchy, 
                TRIP_ID: tripId,
                PAYMENT_DETAILS: approvalComments
            };

            await adminInstance.put(`/expenserequest/UpdateStatus/${hierarchy}`, obj);
            navigate('/ExpenseApprovalList', { state: { message: 'Request approved successfully' } });
        } catch (err) {
            setError('Approval failed. Please try again.');
            setProcessing(false);
        } finally {
            setLoading(false);
        }
    };

    // Handle Cancel actions
    const handleCancel = async () => {
        try {            
            setLoading(true);
            setProcessing(true);
            await adminInstance.post(`/expenserequest/cancel/${tripId}`, null);
            navigate('/MyExpenseRequest', { state: { message: 'Request cancelled successfully' } });
        } catch (err) {
            setError('Cancel failed. Please try again.');
            setProcessing(false);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center mt-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-5">
                <Alert variant="danger">{error}</Alert>
                <Button variant="secondary" onClick={() => navigate(-1)}>
                    Go Back
                </Button>
            </div>
        );
    }

    if (!request) {
        return (
            <div className="container mt-5">
                <Alert variant="warning">Expense request not found</Alert>
                <Button variant="secondary" onClick={() => navigate(-1)}>
                    Go Back
                </Button>
            </div>
        );
    }

    // Helper functions
    const getStatusBadge = () => {
        switch (request.TripStatus) {
            case 'Open': return 'warning';
            case 'Approved': return 'success';
            case 'Completed': return 'success';
            case 'Rejected': return 'danger';
            default: return 'secondary';
        }
    };

    const formatDate = (dateString) => {
        return dateString ? new Date(dateString).toLocaleDateString() : 'N/A';
    };

    const travelTotal = request.ITEMS?.reduce((sum, item) => sum + (item.AMOUNT || 0), 0) || 0;

    return (
        <div className="card aon-card">
            <LoaderOverlay loading={loading} message="Loading" height="500px" />
            <div className="card-header aon-card-header">
                <h4>
                    {hierarchy === "Auditor" ? "Reimbursement Auditing" : "Reimbursement Accounting"}
                </h4>
            </div>
            <div className="card-body aon-card-body">
                <div className="container">
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2>#{request.TripId} | {request.EmployeeName}</h2>
                    <div className="d-flex align-items-center">
                        <Badge bg={getStatusBadge()} className="me-2">
                            {request.TripStatus}
                        </Badge>
                        <span className="text-muted">
                            Created on: {formatDate(request.TripDate)}
                        </span>
                    </div>
                    </div>
                <div>
                {((request.REQ_STATUS === 'Open' || request.REQ_STATUS === 'Approved')) && (
                    <Button variant="danger" onClick={() => handleCancel()}>
                        Cancel Request
                    </Button>)}
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                    Back to List
                            </Button></div>
            </div>

            {/* Employee and Request Info */}
            <Row className="mb-4">
                <Col md={6}>
                    <Card className="h-100">
                        <Card.Body>
                            <Card.Title>Employee Details</Card.Title>
                            <div className="employee-details">
                                        <p><strong>Name:</strong> {request.EmployeeName || 'N/A'}</p>
                                        <p><strong>Employee Code:</strong> {request.EmployeeCode || 'N/A'}</p>
                                        <p><strong>Designation:</strong> {request.EmployeeDesignation || 'N/A'}</p>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="h-100">
                        <Card.Body>
                            <Card.Title>Request Summary</Card.Title>
                            <div className="request-summary">
                                        <p><strong>Trip Name:</strong> {request.TripName || 'N/A'}</p>
                                        <p><strong>No Of Requests:</strong> {request.NoOfRequests || 'N/A'}</p>
                                        <p><strong>Created Date:</strong> {formatDate(request.TripDate) }</p>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Expense Breakdown */}
            <Card className="mb-4">
                <Card.Body>
                    <Card.Title className="mb-4">Expense Summary</Card.Title>

                    {/* Travel Expenses - Only show if items exist */}
                    {false && request.ITEMS?.length > 0 ? (
                        <div className="mb-4">
                            <h5>Travel Expenses</h5>
                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th>From</th>
                                            <th>To</th>
                                            <th>Distance (km)</th>
                                            <th>Mode</th>
                                            <th>Vehicle</th>
                                            <th>Amount (₹)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {request.ITEMS.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.TRV_FROM || 'N/A'}</td>
                                                <td>{item.TRV_TO || 'N/A'}</td>
                                                <td>{item.DISTANCE?.toFixed(2) || 'N/A'}</td>
                                                <td>{item.MODE === 'O' ? 'Own Vehicle' : 'Public Transport'}</td>
                                                <td>{item.VEHICLE || 'N/A'}</td>
                                                <td>₹{item.AMOUNT?.toFixed(2) || '0.00'}</td>
                                            </tr>
                                        ))}
                                        <tr className="table-active">
                                            <td colSpan="5" className="text-end"><strong>Travel Total:</strong></td>
                                            <td><strong>₹{travelTotal.toFixed(2)}</strong></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                                    false && <Alert variant="info" className="mb-4">No travel expenses recorded</Alert>
                    )}

                    {/* Other Expenses */}
                    <Row>
                        <Col md={4}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title>Food</Card.Title>
                                    <Card.Text className="fs-4">
                                        ₹{request.TotalFood?.toFixed(2) || '0.00'}
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                            <Col md={4}>
                                <Card className="mb-3">
                                    <Card.Body>
                                        <Card.Title>Stay</Card.Title>
                                        <Card.Text className="fs-4">
                                            ₹{request.TotalStay?.toFixed(2) || '0.00'}
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        <Col md={4}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title>Travel</Card.Title>
                                    <Card.Text className="fs-4">
                                        ₹{request.TotalTravel?.toFixed(2) || '0.00'}
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        </Row>

                        {/* Additional Other Expenses */}
                        <Row>
                            <Col md={4}>
                                <Card className="mb-3">
                                    <Card.Body>
                                        <Card.Title>Printing & Stationery Charge</Card.Title>
                                        <Card.Text className="fs-4">
                                            ₹{request.TotalStationary?.toFixed(2) || '0.00'}
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="mb-3">
                                    <Card.Body>
                                        <Card.Title>Courier Charges</Card.Title>
                                        <Card.Text className="fs-4">
                                            ₹{request.TotalCourier?.toFixed(2) || '0.00'}
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="mb-3">
                                    <Card.Body>
                                        <Card.Title>Other Expenses</Card.Title>
                                        <Card.Text className="fs-4">
                                            ₹{request.TotalOtherExpense?.toFixed(2) || '0.00'}
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                    {/* Grand Total */}
                    <Card className="bg-light">
                        <Card.Body className="text-end">
                            <h4 className="mb-0">
                                Total Amount: ₹{request.GrandTotal?.toFixed(2) || '0.00'}
                            </h4>
                        </Card.Body>
                    </Card>
                </Card.Body>
            </Card>

            {/* Supporting Images */}
            {false &&<Card className="mb-4">
                <Card.Body>
                    <Card.Title>Supporting Documents</Card.Title>
                    {request.REQ_IMAGES?.length > 0 ? (
                        <div className="d-flex flex-wrap">
                            {request.REQ_IMAGES.map((img, index) => (
                                <div key={index} className="me-3 mb-3" style={{ width: '150px', cursor: 'pointer' }}>
                                    <Image
                                        src={img}
                                        thumbnail
                                        fluid
                                        onClick={() => {
                                            setSelectedImage(img);
                                            setShowImageModal(true);
                                        }}
                                    />
                                    <div className="text-center small mt-1">Document {index + 1}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Alert variant="info">No supporting documents attached</Alert>
                    )}
                </Card.Body>
            </Card>}

            {/* Approval Section (only for pending requests) */}
            {(
                <Card>
                    <Card.Body>
                        <Card.Title>Payment </Card.Title>
                        <Form.Group className="mb-3">
                            <Form.Label>Payment Details*</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={6}
                                value={approvalComments}
                                onChange={(e) => setApprovalComments(e.target.value)}
                                placeholder="Enter your payment details..."
                                disabled={hierarchy === "Auditor"}
                            />
                        </Form.Group>
                        {hierarchy === "Auditor" && <Form.Group className="mb-3 d-flex align-items-center">
                            <Form.Check
                                type="checkbox"
                                id="audit-confirm-checkbox"
                                label="I confirm that I have audited and verified the above details."
                                // Add state for checkbox
                                checked={auditAct}
                                onChange={e => {
                                    setAuditAct(e.target.checked);
                                    // force update if needed, or use a useState for auditConfirmChecked
                                }}
                            />
                        </Form.Group>}
                        <div className="d-flex justify-content-end gap-2">                            
                            <Button
                                variant="success"
                                onClick={() => handleApproveOrReject(hierarchy === "Admin" ? "Completed" : "Closed")}
                                disabled={processing}
                            >
                                {processing ? <Spinner size="sm" /> : hierarchy === "Admin" ? 'Complete Request' : 'Complete Audit'}
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            )}
            
        </div>
        </div>
        </div>
    );
};

export default ExpenseAdminApprovalPage;